import {
  updateInquiryItemCount,
  getInquiry as apiGetInquiry,
  getBulkInquiry as apiGetBulkInquiry,
  setInquiryItemCount
} from './Api';
import EventBus from './EventBus';

let _inquiries = {};
let getInquiryPromise = {};
let getBulkInquiryPromise = null;
const inquiryItemsUpdateStack = [];
let cachedVariantItemCounts = {};
const variantAddingInquiry = {};

export const setItemCount = (inquiry, additionalItemCount) =>
  updateInquiryItemCount({
    inquiryId: inquiry.id,
    itemCount: additionalItemCount
  });

export const setNonAdditionalItemCount = (inquiry, itemCount) =>
  setInquiryItemCount({
    inquiryId: inquiry.id,
    itemCount
  });

export const getCachedInquiry = (id) => _inquiries[id] || null;

export const getInquiry = (id, refresh, passedInquiry) => {
  if (getInquiryPromise[id] && !refresh) {
    return getInquiryPromise[id];
  }

  getInquiryPromise[id] = new Promise((resolve) => {
    (passedInquiry ? Promise.resolve({ inquiry: passedInquiry }) : apiGetInquiry(id))
      .then(({ inquiry }) => {
        if (!inquiry || !inquiry.product || !inquiry.product.id) {
          EventBus.notify(EventBus.TYPE.INQUIRY_DELETED);
          delete _inquiries[id];
          delete getInquiryPromise[id];
          return Promise.resolve(null);
        }

        return Promise.resolve(inquiry);
      })
      .then((inquiry) => {
        _inquiries[inquiry.id] = inquiry;
        resolve(inquiry);
      })
      .catch(() => {
        resolve(null);
      });
  });

  return getInquiryPromise[id];
};

export const getBulkInquiry = (orderId, refresh, passedBulkInquiry) => {
  if (getBulkInquiryPromise && !refresh) {
    return getBulkInquiryPromise;
  }

  getBulkInquiryPromise = new Promise((resolve) => {
    (passedBulkInquiry
      ? Promise.resolve({
        inquiry: passedBulkInquiry
      })
      : apiGetBulkInquiry(orderId)
    )
      .then(({ inquiries }) => {
        if (!inquiries || inquiries.length === 0) {
          EventBus.notify(EventBus.TYPE.INQUIRY_DELETED);
          _inquiries = {};
          getInquiryPromise = {};
          return Promise.resolve(null);
        }

        const inquiriesObj = {};
        inquiries.forEach((inquiry) => {
          inquiriesObj[inquiry.id] = inquiry;
        });

        return Promise.resolve(inquiriesObj);
      })
      .then((inquiries) => {
        _inquiries = {
          ..._inquiries,
          ...inquiries
        };
        resolve(inquiries);
      })
      .catch(() => {
        resolve(null);
      });
  });

  return getBulkInquiryPromise;
};

export const setCacheItemCount = (inquiryId, itemCount) => {
  if (_inquiries[inquiryId]) {
    _inquiries[inquiryId].itemCount = itemCount;
    EventBus.notify(EventBus.TYPE.ORDER_CHANGED);
  }
};

export const addToCacheItemCount = (inquiryId, additionalItemCount) => {
  if (_inquiries[inquiryId] && _inquiries[inquiryId].maxItemCount > additionalItemCount) {
    _inquiries[inquiryId].itemCount += additionalItemCount;
    EventBus.notify(EventBus.TYPE.ORDER_CHANGED);
  }
};

export const pushInquiryItemsUpdateStack = () => {
  inquiryItemsUpdateStack.push(true);
  EventBus.notify(EventBus.TYPE.INQUIRY_ITEMS_UPDATE_STACK_CHANGED);
};

export const popInquiryItemsUpdateStack = () => {
  inquiryItemsUpdateStack.pop();
  EventBus.notify(EventBus.TYPE.INQUIRY_ITEMS_UPDATE_STACK_CHANGED);
};

export const getInquiryItemsUpdateStack = () => inquiryItemsUpdateStack;

export const orderFinalized = () => {
  _inquiries = {};
  getInquiryPromise = {};
};

export const setCachedVariantItemCount = (variantId, itemCount) => {
  cachedVariantItemCounts[variantId] = itemCount;
};

export const getCachedVariantItemCount = (variantId) => cachedVariantItemCounts[variantId] || 0;

export const resetCachedVariantItemCount = (variantIds) => {
  if (!Array.isArray(variantIds)) {
    cachedVariantItemCounts = {};
    return;
  }

  variantIds.forEach((id) => {
    delete cachedVariantItemCounts[id];
  });
};

export const setVariantAddingInquiry = (variantId, adding) => {
  if (adding) {
    variantAddingInquiry[variantId] = true;
  } else {
    delete variantAddingInquiry[variantId];
  }
};

export const getVariantAddingInquiry = (variantId) => !!variantAddingInquiry[variantId];

export const getInquiryMoq = (inquiry) => {
  if (!inquiry) {
    return null;
  }

  return inquiry.minOrderQuantity || 1;
};

export const updateInquiryCache = ({ inquiryId, updates }) => {
  const cachedInquiry = getCachedInquiry(inquiryId);
  if (!cachedInquiry || !Object.keys(updates || {}).length) {
    return;
  }

  _inquiries[inquiryId] = { ...cachedInquiry, ...updates };
};

export const isInquiryInStock = (inquiryId) => {
  const inquiry = _inquiries[inquiryId];
  if (!inquiry) {
    return true;
  }

  const { product } = inquiry;
  if (!product) {
    return true;
  }

  return (
    product.productStockCount === null ||
    inquiry.maxItemCount === null ||
    (product.productStockCount > 0 && inquiry.maxItemCount >= inquiry.itemCount)
  );
};

export const updateInquiryItemCountFromMoq = ({ inquiryId, prevItemCount }) =>
  new Promise((resolve, reject) => {
    const updatedInquiry = getCachedInquiry(inquiryId);
    const minimumOrderQuantity = getInquiryMoq(updatedInquiry);
    if (typeof minimumOrderQuantity !== 'number') {
      return resolve();
    }

    setCacheItemCount(inquiryId, minimumOrderQuantity);
    setItemCount(updatedInquiry, minimumOrderQuantity - prevItemCount)
      .then(() => resolve())
      .catch(() => {
        setCacheItemCount(inquiryId, prevItemCount);
        reject();
      });
  });
